import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Snackbar } from '@mui/material';

const NotifyContext = createContext(null);

// Errors stay up long enough to read a server message; success is a glance.
// Snackbar pauses the timer while the pointer is over it, so a longer message
// is never yanked away mid-read.
const DURATION = { error: 8000, warning: 6000, info: 4000, success: 3000 };

export const useNotify = () => {
  const context = useContext(NotifyContext);
  if (!context) {
    throw new Error('useNotify must be used within a NotifyProvider');
  }
  return context;
};

/**
 * One app-wide Snackbar replacing window.alert(). Messages queue rather than
 * overwrite, so a second failure can't hide the first before anyone saw it.
 */
export const NotifyProvider = ({ children }) => {
  const [queue, setQueue] = useState([]);
  const [open, setOpen] = useState(false);
  const nextKey = useRef(0);
  const current = queue[0];

  const push = useCallback((severity, message) => {
    if (!message) return;
    nextKey.current += 1;
    setQueue((prev) => [...prev, { key: nextKey.current, severity, message: String(message) }]);
  }, []);

  const handleClose = (event, reason) => {
    // A click elsewhere on the page is not "I read it" — keep errors until they time out
    // or are dismissed with the close button.
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  // Advance only after the exit transition finishes, so the next message enters fresh
  // instead of swapping text inside a snackbar that is mid-exit.
  const handleExited = () => setQueue((prev) => prev.slice(1));

  useEffect(() => {
    if (current) setOpen(true);
  }, [current?.key]); // eslint-disable-line react-hooks/exhaustive-deps

  const api = useMemo(() => ({
    error: (message) => push('error', message),
    warning: (message) => push('warning', message),
    info: (message) => push('info', message),
    success: (message) => push('success', message),
  }), [push]);

  return (
    <NotifyContext.Provider value={api}>
      {children}
      <Snackbar
        key={current?.key}
        open={open && !!current}
        autoHideDuration={current ? DURATION[current.severity] : null}
        onClose={handleClose}
        TransitionProps={{ onExited: handleExited }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        // Above every dialog, including HorseModalContext's stacked modals (1300 + 20/level).
        sx={{ zIndex: (theme) => theme.zIndex.tooltip }}
      >
        {current ? (
          <Alert
            onClose={handleClose}
            severity={current.severity}
            variant="filled"
            sx={{ width: '100%', maxWidth: 560, alignItems: 'center' }}
          >
            {current.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </NotifyContext.Provider>
  );
};
